import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/StatusBadge";
import { SupplierSelect } from "../../components/SupplierSelect";
import { EmptyState } from "../../components/EmptyState";
import {
  ArrowLeft, RefreshCw, StickyNote, Printer, Image as ImageIcon, FileVideo, Wrench,
  Truck, Building2, Receipt, Clock, Send, MessageSquare, Upload, FileText, Plus, Lock, PackageOpen,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../../lib/api";
import {
  INTERNAL_STATUSES, VENDOR_DECISIONS, SHIPMENT_DIRECTIONS, money, fmtDate, fmtDateTime, replacementBadgeClass,
} from "../../lib/statuses";
import { useAuth } from "../../context/AuthContext";

const TABS = [
  { key: "overview", label: "Overview", icon: FileText },
  { key: "files", label: "Customer Files", icon: ImageIcon },
  { key: "testing", label: "Internal Testing", icon: Wrench },
  { key: "shipping", label: "Shipping", icon: Truck },
  { key: "vendor", label: "Vendor", icon: Building2 },
  { key: "credit", label: "Credit Note", icon: Receipt },
  { key: "timeline", label: "Timeline", icon: Clock },
];

export default function RmaCase() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [statusOpen, setStatusOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusComment, setStatusComment] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/claims/${id}`);
    setData(data);
    setNewStatus(data.claim.status);
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async () => {
    try {
      await api.put(`/admin/claims/${id}/status`, { status: newStatus, comment: statusComment });
      toast.success("Status updated");
      setStatusOpen(false); setStatusComment(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/admin/claims/${id}/notes`, { note }); toast.success("Note added"); setNoteOpen(false); setNote(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendMessage = async () => {
    if (!message.trim()) return;
    try { await api.post(`/admin/claims/${id}/messages`, { message }); toast.success("Message sent to customer"); setMsgOpen(false); setMessage(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <AdminLayout><Skeleton className="h-10 w-64" /><div className="mt-6 grid gap-4 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div></AdminLayout>;

  const c = data.claim;

  return (
    <AdminLayout>
      <button onClick={() => navigate("/admin/warranty/claims")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to claims
      </button>

      {/* Header */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-mono text-2xl font-extrabold tracking-tight text-slate-900">{c.rma_number}</h1>
              <StatusBadge status={c.status} testId="case-status-badge" />
            </div>
            <p className="mt-1 text-sm text-slate-500">{c.product_name} • {c.customer_name} • {c.days_open} days open</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setStatusOpen(true)} data-testid="update-status-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><RefreshCw className="h-4 w-4" /> Update Status</Button>
            <Button onClick={() => setNoteOpen(true)} data-testid="add-note-btn" variant="outline" className="gap-1.5"><StickyNote className="h-4 w-4" /> Add Note</Button>
            <Button onClick={() => setMsgOpen(true)} data-testid="message-customer-btn" variant="outline" className="gap-1.5"><MessageSquare className="h-4 w-4" /> Message</Button>
            <Button onClick={() => window.print()} variant="outline" className="gap-1.5"><Printer className="h-4 w-4" /> Print</Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview data={data} onReload={load} />}
      {tab === "files" && <CustomerFiles files={data.files} />}
      {tab === "testing" && <Testing claimId={id} testing={data.testing} onReload={load} />}
      {tab === "shipping" && <Shipping claimId={id} shipments={data.shipments} onReload={load} />}
      {tab === "vendor" && <Vendor claimId={id} data={data} onReload={load} />}
      {tab === "credit" && <CreditTab data={data} onReload={load} />}
      {tab === "timeline" && <Timeline timeline={data.timeline} history={data.status_history} />}

      {/* Status dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Update Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-sm font-medium">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{INTERNAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-sm font-medium">Comment (optional)</Label><Textarea data-testid="status-comment" value={statusComment} onChange={(e) => setStatusComment(e.target.value)} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button onClick={updateStatus} data-testid="confirm-status-btn" className="bg-blue-600 hover:bg-blue-700">Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4 text-slate-400" /> Internal Note</DialogTitle></DialogHeader>
          <p className="-mt-2 text-xs text-slate-400">Internal notes are never visible to customers.</p>
          <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Add an internal note..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
            <Button onClick={addNote} data-testid="confirm-note-btn" className="bg-blue-600 hover:bg-blue-700">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-blue-600" /> Message Customer</DialogTitle></DialogHeader>
          <p className="-mt-2 text-xs text-slate-400">This message is visible to the customer in their tracking page.</p>
          <Textarea data-testid="message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="e.g. Please send the product to IDEA." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)}>Cancel</Button>
            <Button onClick={sendMessage} data-testid="confirm-message-btn" className="bg-blue-600 hover:bg-blue-700">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right font-medium text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function Overview({ data, onReload }) {
  const c = data.claim;
  const { hasRole } = useAuth();
  const canEdit = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "WARRANTY_STAFF");
  const [edit, setEdit] = useState(false);
  const [editClaimOpen, setEditClaimOpen] = useState(false);

  const toggleReadyToShip = async () => {
    try {
      await api.put(`/admin/claims/${c.id}/ready-to-ship`, { ready: !c.ready_to_ship });
      toast.success(c.ready_to_ship ? "Removed from Ready to Ship" : "Marked Ready to Ship");
      onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [sup, setSup] = useState(c.supplier_id || "");
  const [emp, setEmp] = useState(c.assigned_employee || "");
  const [val, setVal] = useState(c.claim_value || "");

  const save = async () => {
    try {
      await api.put(`/admin/claims/${c.id}`, { supplier_id: sup || undefined, assigned_employee: emp, claim_value: val });
      toast.success("Claim updated"); setEdit(false); onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      {canEdit && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" data-testid="edit-claim-btn" onClick={() => setEditClaimOpen(true)}>Edit Claim</Button>
          <Button size="sm" data-testid="ready-to-ship-btn" onClick={toggleReadyToShip}
            className={c.ready_to_ship ? "gap-1.5 bg-emerald-600 hover:bg-emerald-700" : "gap-1.5 bg-blue-600 hover:bg-blue-700"}>
            <PackageOpen className="h-4 w-4" /> {c.ready_to_ship ? "Ready to Ship ✓ (Unmark)" : "Mark Ready to Ship"}
          </Button>
        </div>
      )}
      <EditClaimDialog claim={c} open={editClaimOpen} onOpenChange={setEditClaimOpen} onSaved={onReload} />
      <div className="grid gap-5 lg:grid-cols-3">
        <Section title="Customer">
          <Row label="Name" value={c.customer_name} />
          <Row label="Mobile" value={c.mobile} mono />
          <Row label="Email" value={c.email} />
        </Section>
        <Section title="Order">
          <Row label="Order Number" value={c.order_number} mono />
          <Row label="Invoice Number" value={c.invoice_number} mono />
          <Row label="Purchase Date" value={fmtDate(c.purchase_date)} />
        </Section>
        <Section title="Product">
          <Row label="Product" value={c.product_name} />
          <Row label="Part Name" value={c.part_name} />
          <Row label="Brand" value={c.brand} />
          <Row label="SKU" value={c.sku} mono />
          <Row label="Serial" value={c.serial_number} mono />
          {c.url && (
            <div className="flex justify-between gap-4 text-sm">
              <span className="text-slate-500">URL</span>
              <a href={c.url} target="_blank" rel="noreferrer" data-testid="rma-url-link" className="max-w-[60%] truncate text-right font-medium text-blue-700 hover:underline">{c.url}</a>
            </div>
          )}
          <Row label="Purchase Price" value={money(c.purchase_price, c.currency)} />
          <Row label="Warranty Expiry" value={fmtDate(c.warranty_expiry)} />
        </Section>
        <Section title="Supplier & Assignment">
          {!edit ? (
            <>
              <Row label="Supplier" value={c.supplier_name} />
              <Row label="Assigned" value={c.assigned_employee} />
              <Row label="Claim Value" value={money(c.claim_value, c.currency)} />
              {c.product_cost ? <Row label="Product Cost" value={money(c.product_cost, c.product_cost_currency || c.currency)} /> : null}
              {canEdit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="edit-overview-btn" className="mt-2">Edit</Button>}
            </>
          ) : (
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs">Supplier</Label><SupplierSelect value={sup} onChange={setSup} testId="overview-supplier" /></div>
              <div><Label className="mb-1 block text-xs">Assigned Employee</Label><Input value={emp} onChange={(e) => setEmp(e.target.value)} /></div>
              <div><Label className="mb-1 block text-xs">Claim Value</Label><Input type="number" value={val} onChange={(e) => setVal(e.target.value)} /></div>
              <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="save-overview-btn" className="bg-blue-600 hover:bg-blue-700">Save</Button><Button size="sm" variant="outline" onClick={() => setEdit(false)}>Cancel</Button></div>
            </div>
          )}
        </Section>
        <Section title="Problem">
          <Row label="Category" value={c.problem_category} />
          <div className="pt-1 text-sm text-slate-600">{c.problem_description || "—"}</div>
        </Section>
        <InternalNotes notes={data.notes} />
      </div>

      {data.messages?.length > 0 && (
        <Section title="Customer Messages">
          {data.messages.map((m) => (
            <div key={m.id} className="rounded-lg bg-blue-50 p-3">
              <p className="text-sm text-slate-700">{m.message}</p>
              <p className="mt-1 text-xs text-slate-400">{m.employee} • {fmtDateTime(m.created_at)}</p>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function EditClaimDialog({ claim, open, onOpenChange, onSaved }) {
  const FIELDS = [
    ["order_number", "Order / Invoice Number"],
    ["customer_name", "Customer Name"],
    ["mobile", "WhatsApp Number"],
    ["product_name", "Product Name"],
    ["part_name", "Part Name"],
    ["brand", "Brand"],
    ["serial_number", "Serial Number"],
    ["url", "URL"],
  ];
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      const init = {};
      FIELDS.forEach(([k]) => { init[k] = claim[k] || ""; });
      init.problem_description = claim.problem_description || "";
      setForm(init);
    }
  }, [open, claim?.id]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/claims/${claim.id}/details`, form);
      toast.success("Claim details updated"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-claim-dialog">
        <DialogHeader><DialogTitle>Edit Claim — {claim.rma_number}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">RMA Number <span className="font-mono font-semibold text-slate-700">{claim.rma_number}</span> cannot be changed.</div>
        <div className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "url" ? "sm:col-span-2" : ""}>
              <Label className="mb-1.5 block text-sm font-medium">{label}</Label>
              <Input data-testid={`edit-${k}`} value={form[k] || ""} onChange={set(k)} placeholder={k === "url" ? "https://..." : ""} />
            </div>
          ))}
          <div className="sm:col-span-2"><Label className="mb-1.5 block text-sm font-medium">Problem Description</Label><Textarea data-testid="edit-problem" rows={3} value={form.problem_description || ""} onChange={set("problem_description")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-edit-claim-btn" className="bg-blue-600 hover:bg-blue-700">{saving ? "Saving..." : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InternalNotes({ notes }) {  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-1">
      <h3 className="mb-3 flex items-center gap-1.5 font-display text-sm font-semibold uppercase tracking-wide text-slate-400"><Lock className="h-3.5 w-3.5" /> Internal Notes</h3>
      <div className="max-h-56 space-y-2 overflow-y-auto">
        {notes.length === 0 && <p className="text-sm text-slate-400">No internal notes yet.</p>}
        {notes.map((n) => (
          <div key={n.id} className="rounded-lg bg-slate-50 p-3">
            <p className="text-sm text-slate-700">{n.note}</p>
            <p className="mt-1 text-xs text-slate-400">{n.employee} • {fmtDateTime(n.created_at)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerFiles({ files }) {
  const serial = files.filter((f) => f.category === "serial_photo");
  const video = files.filter((f) => f.category === "problem_video");
  const extra = files.filter((f) => f.category === "additional_photo");
  const isImg = (f) => (f.content_type || "").startsWith("image");

  if (files.length === 0) return <EmptyState icon={ImageIcon} title="No customer files" testId="files-empty" />;

  return (
    <div className="space-y-6">
      <FileGroup title="Serial Number Photo" files={serial} isImg={isImg} />
      <FileGroup title="Problem Video" files={video} isImg={isImg} />
      {extra.length > 0 && <FileGroup title="Additional Photos" files={extra} isImg={isImg} />}
    </div>
  );
}
function FileGroup({ title, files, isImg }) {
  if (!files.length) return null;
  return (
    <div>
      <h3 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {files.map((f) => (
          <div key={f.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {isImg(f) ? (
              <a href={fileUrl(f.storage_path)} target="_blank" rel="noreferrer"><img src={fileUrl(f.storage_path)} alt={f.original_filename} className="h-48 w-full object-cover" /></a>
            ) : (
              <video src={fileUrl(f.storage_path)} controls className="h-48 w-full bg-black object-contain" />
            )}
            <div className="truncate px-3 py-2 text-xs text-slate-500">{f.original_filename}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Testing({ claimId, testing, onReload }) {
  const { hasRole } = useAuth();
  const canTest = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "TECHNICIAN", "WARRANTY_STAFF");
  const [open, setOpen] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [form, setForm] = useState({ received_date: "", physical_condition: "", serial_verified: false, issue_confirmed: false, technician_diagnosis: "", test_result: "", technician_name: "" });

  const save = async () => {
    try {
      await api.post(`/admin/claims/${claimId}/testing`, form);
      if (videoFile) {
        const fd = new FormData();
        fd.append("file", videoFile);
        fd.append("visibility", "internal");
        fd.append("category", "testing_video");
        await api.post(`/admin/claims/${claimId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Testing recorded"); setOpen(false); setVideoFile(null); onReload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {canTest && <Button onClick={() => setOpen(true)} data-testid="add-testing-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Record Test</Button>}
      {testing.length === 0 ? <EmptyState icon={Wrench} title="No testing records" description="Record technician inspection when the product arrives." testId="testing-empty" /> :
        testing.map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Row label="Received Date" value={fmtDate(t.received_date)} />
              <Row label="Technician" value={t.technician_name} />
              <Row label="Physical Condition" value={t.physical_condition} />
              <Row label="Serial Verified" value={t.serial_verified ? "YES" : "NO"} />
              <Row label="Issue Confirmed" value={t.issue_confirmed ? "YES" : "NO"} />
            </div>
            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700"><span className="font-semibold">Diagnosis Report: </span>{t.technician_diagnosis || "—"}</div>
            <p className="mt-2 text-xs text-slate-400">Recorded by {t.created_by} • {fmtDateTime(t.created_at)}</p>
          </div>
        ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Record Internal Testing</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Received Date</Label><Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} data-testid="test-received-date" /></div>
              <div><Label className="mb-1 block text-xs">Technician Name</Label><Input value={form.technician_name} onChange={(e) => setForm({ ...form, technician_name: e.target.value })} data-testid="test-technician" /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Physical Condition</Label><Input value={form.physical_condition} onChange={(e) => setForm({ ...form, physical_condition: e.target.value })} /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.serial_verified} onCheckedChange={(v) => setForm({ ...form, serial_verified: v })} data-testid="test-serial-verified" /> Serial Verified</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.issue_confirmed} onCheckedChange={(v) => setForm({ ...form, issue_confirmed: v })} data-testid="test-issue-confirmed" /> Issue Confirmed</label>
            </div>
            <div><Label className="mb-1 block text-xs">Diagnosis Report</Label><Textarea value={form.technician_diagnosis} onChange={(e) => setForm({ ...form, technician_diagnosis: e.target.value })} rows={3} data-testid="test-diagnosis" /></div>
            <div>
              <Label className="mb-1 block text-xs">Upload Issue Video (internal only)</Label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 hover:border-blue-400">
                <Upload className="h-4 w-4" /> {videoFile ? videoFile.name : "Choose video (MP4 / MOV)"}
                <input type="file" accept="video/mp4,video/quicktime,.mov" className="hidden" data-testid="test-issue-video" onChange={(e) => setVideoFile(e.target.files[0])} />
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-testing-btn" className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Shipping({ claimId, shipments, onReload }) {
  const { hasRole } = useAuth();
  const canShip = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "WARRANTY_STAFF");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ direction: "CUSTOMER_TO_IDEA", courier: "", tracking_number: "", shipping_date: "", expected_arrival: "", actual_arrival: "", shipping_cost: "", currency: "OMR", tracking_url: "", notes: "" });
  const dirLabel = (d) => SHIPMENT_DIRECTIONS.find((s) => s.value === d)?.label || d;

  const save = async () => {
    try { await api.post(`/admin/claims/${claimId}/shipments`, form); toast.success("Shipment added"); setOpen(false); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {canShip && <Button onClick={() => setOpen(true)} data-testid="add-shipment-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Add Shipment</Button>}
      {shipments.length === 0 ? <EmptyState icon={Truck} title="No shipments" description="Track movement between customer, IDEA and vendor." testId="shipping-empty" /> :
        <div className="space-y-3">
          {shipments.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">{dirLabel(s.direction)}</span>
                <span className="font-mono text-sm text-slate-600">{money(s.shipping_cost, s.currency)}</span>
              </div>
              <div className="grid gap-2 text-sm sm:grid-cols-4">
                <Row label="Courier" value={s.courier} />
                <Row label="Tracking" value={s.tracking_number} mono />
                <Row label="Shipped" value={fmtDate(s.shipping_date)} />
                <Row label="Arrived" value={fmtDate(s.actual_arrival)} />
              </div>
              {s.tracking_url && <a href={s.tracking_url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-blue-600 hover:underline">Track shipment →</a>}
            </div>
          ))}
        </div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Add Shipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs">Direction</Label>
              <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                <SelectTrigger data-testid="shipment-direction"><SelectValue /></SelectTrigger>
                <SelectContent>{SHIPMENT_DIRECTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Courier</Label><Input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} data-testid="shipment-courier" /></div>
              <div><Label className="mb-1 block text-xs">Tracking Number</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Shipping Date</Label><Input type="date" value={form.shipping_date} onChange={(e) => setForm({ ...form, shipping_date: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Expected Arrival</Label><Input type="date" value={form.expected_arrival} onChange={(e) => setForm({ ...form, expected_arrival: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Actual Arrival</Label><Input type="date" value={form.actual_arrival} onChange={(e) => setForm({ ...form, actual_arrival: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Shipping Cost</Label><Input type="number" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Tracking URL</Label><Input value={form.tracking_url} onChange={(e) => setForm({ ...form, tracking_url: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-shipment-btn" className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Vendor({ claimId, data, onReload }) {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const canVendor = hasRole("SUPER_ADMIN", "WARRANTY_MANAGER", "FINANCE");
  const vc = data.vendor_claim;
  const [createOpen, setCreateOpen] = useState(false);
  const [decOpen, setDecOpen] = useState(false);
  const [cform, setCform] = useState({ supplier_id: data.claim.supplier_id || "", purchase_invoice_number: "", purchase_date: "", part_name: data.claim.part_name || data.claim.product_name || "", product_cost: "", product_cost_currency: "", notes: "" });
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [dform, setDform] = useState({ vendor_status: "CREDIT APPROVED", decision_date: "", approved_amount: "", decision_reference: "", decision_notes: "" });

  const createVC = async () => {
    if (!cform.supplier_id) { toast.error("Select supplier"); return; }
    try {
      const { data: vc } = await api.post(`/admin/vendor-claims`, { claim_id: claimId, ...cform });
      if (invoiceFile && vc?.id) {
        const fd = new FormData(); fd.append("file", invoiceFile);
        await api.post(`/admin/vendor-claims/${vc.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Escalated to supplier"); setCreateOpen(false); onReload();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const recordDec = async () => {
    try { await api.put(`/admin/vendor-claims/${vc.id}/decision`, dform); toast.success("Decision recorded"); setDecOpen(false); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadVF = async (file) => {
    if (!file) return;
    try { const fd = new FormData(); fd.append("file", file); await api.post(`/admin/vendor-claims/${vc.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("File uploaded"); onReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!vc) {
    return (
      <div>
        <EmptyState icon={Building2} title="Not yet escalated to a supplier"
          description="Send this RMA to a supplier to begin the vendor claim process."
          action={canVendor && <Button onClick={() => setCreateOpen(true)} data-testid="escalate-vendor-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Send className="h-4 w-4" /> Escalate to Supplier</Button>}
          testId="vendor-empty" />
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Escalate to Supplier</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="mb-1 block text-xs">Supplier</Label><SupplierSelect value={cform.supplier_id} onChange={(id) => setCform({ ...cform, supplier_id: id })} testId="vc-supplier" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-1 block text-xs">Purchase Invoice Number</Label><Input value={cform.purchase_invoice_number} onChange={(e) => setCform({ ...cform, purchase_invoice_number: e.target.value })} data-testid="vc-invoice-number" /></div>
                <div><Label className="mb-1 block text-xs">Purchase Date</Label><Input type="date" value={cform.purchase_date} onChange={(e) => setCform({ ...cform, purchase_date: e.target.value })} /></div>
                <div className="col-span-2"><Label className="mb-1 block text-xs">Part Name</Label><Input value={cform.part_name} onChange={(e) => setCform({ ...cform, part_name: e.target.value })} data-testid="vc-part-name" /></div>
                <div><Label className="mb-1 block text-xs">Product Cost</Label><Input type="number" step="0.001" value={cform.product_cost} onChange={(e) => setCform({ ...cform, product_cost: e.target.value })} data-testid="vc-product-cost" placeholder="0.000" /></div>
                <div><Label className="mb-1 block text-xs">Cost Currency</Label><Input value={cform.product_cost_currency} onChange={(e) => setCform({ ...cform, product_cost_currency: e.target.value })} data-testid="vc-cost-currency" placeholder="Supplier currency" /></div>
              </div>
              <div><Label className="mb-1 block text-xs">Notes</Label><Textarea value={cform.notes} onChange={(e) => setCform({ ...cform, notes: e.target.value })} rows={2} /></div>
              <div>
                <Label className="mb-1 block text-xs">Invoice Upload (PDF / JPG / PNG)</Label>
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 hover:border-blue-400">
                  <Upload className="h-4 w-4" /> {invoiceFile ? invoiceFile.name : "Choose invoice file"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf" className="hidden" data-testid="vc-invoice-upload" onChange={(e) => setInvoiceFile(e.target.files[0])} />
                </label>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={createVC} data-testid="confirm-escalate-btn" className="bg-blue-600 hover:bg-blue-700">Escalate</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const decColor = (d) => d === "PENDING" ? "bg-amber-50 text-amber-700" : d === "REJECTED" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700";

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-base font-semibold text-slate-900">{vc.supplier_name}</h3>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${decColor(vc.vendor_status)}`}>{vc.vendor_status}</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Row label="Supplier Claim #" value={vc.supplier_claim_number} mono />
          <Row label="Date Submitted" value={fmtDate(vc.date_submitted)} />
          <Row label="Claim Amount" value={money(vc.claim_amount, vc.currency)} />
          <Row label="Supplier Contact" value={vc.supplier_contact} />
          <Row label="Reference" value={vc.supplier_reference} />
          <Row label="Last Follow-up" value={fmtDate(vc.last_follow_up)} />
          <Row label="Next Follow-up" value={fmtDate(vc.next_follow_up)} />
          <Row label="Approved Amount" value={money(vc.approved_amount, vc.currency)} />
          <Row label="Decision Date" value={fmtDate(vc.decision_date)} />
        </div>
        {canVendor && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setDecOpen(true)} data-testid="record-decision-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700">Record Decision</Button>
            <label className="cursor-pointer">
              <Button size="sm" variant="outline" className="gap-1.5 pointer-events-none"><Upload className="h-4 w-4" /> Attach File</Button>
              <input type="file" className="hidden" data-testid="vendor-file-upload" onChange={(e) => uploadVF(e.target.files[0])} />
            </label>
          </div>
        )}
      </div>

      {data.replacement && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="rma-replacement-card">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-slate-900">Replacement</h3>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${replacementBadgeClass(data.replacement.replacement_status)}`}>{data.replacement.replacement_status}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Row label="Supplier" value={data.replacement.supplier_name} />
            <Row label="Approved Date" value={fmtDate(data.replacement.approved_date)} />
            <Row label="Replacement Serial" value={data.replacement.replacement_serial_number} mono />
            <Row label="Received" value={data.replacement.received_date ? fmtDate(data.replacement.received_date) : "Pending"} />
            <Row label="Handover" value={data.replacement.handover_date ? fmtDate(data.replacement.handover_date) : "Pending"} />
          </div>
          <button onClick={() => navigate("/admin/replacements")} className="mt-3 text-xs font-semibold text-blue-700 hover:underline">Manage in Replacements →</button>
        </div>
      )}

      <Dialog open={decOpen} onOpenChange={setDecOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Record Vendor Decision</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs">Decision</Label>
              <Select value={dform.vendor_status} onValueChange={(v) => setDform({ ...dform, vendor_status: v })}>
                <SelectTrigger data-testid="decision-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VENDOR_DECISIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Decision Date</Label><Input type="date" value={dform.decision_date} onChange={(e) => setDform({ ...dform, decision_date: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Approved Amount</Label><Input type="number" value={dform.approved_amount} onChange={(e) => setDform({ ...dform, approved_amount: e.target.value })} data-testid="decision-amount" /></div>
              <div><Label className="mb-1 block text-xs">Reference Number</Label><Input value={dform.decision_reference} onChange={(e) => setDform({ ...dform, decision_reference: e.target.value })} /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Notes</Label><Textarea value={dform.decision_notes} onChange={(e) => setDform({ ...dform, decision_notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDecOpen(false)}>Cancel</Button><Button onClick={recordDec} data-testid="confirm-decision-btn" className="bg-blue-600 hover:bg-blue-700">Save Decision</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreditTab({ data, onReload }) {
  const { hasRole } = useAuth();
  const canManage = hasRole("SUPER_ADMIN", "FINANCE");
  const cns = data.credit_notes || [];
  const c = data.claim;
  const [open, setOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [form, setForm] = useState({ cn_number: "", issue_date: "", currency: c.currency || "OMR", original_amount: "", notes: "" });

  const create = async () => {
    if (!c.supplier_id) { toast.error("Escalate to a supplier first (no supplier linked)"); return; }
    if (!form.cn_number || !form.original_amount) { toast.error("CN number and amount are required"); return; }
    try {
      const { data: cn } = await api.post(`/admin/credit-notes`, {
        cn_number: form.cn_number, supplier_id: c.supplier_id, issue_date: form.issue_date,
        currency: form.currency, original_amount: form.original_amount, notes: form.notes, claim_ids: [c.id],
      });
      if (pdfFile && cn?.id) {
        const fd = new FormData(); fd.append("file", pdfFile);
        await api.post(`/admin/credit-notes/${cn.id}/pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Credit note created & linked"); setOpen(false); setPdfFile(null);
      setForm({ cn_number: "", issue_date: "", currency: c.currency || "OMR", original_amount: "", notes: "" });
      onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Credit Notes are created here and auto-linked to this RMA, product, serial & supplier.</p>
          <Button onClick={() => setOpen(true)} data-testid="add-cn-from-rma-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Add Credit Note</Button>
        </div>
      )}
      {cns.length === 0 && <EmptyState icon={Receipt} title="No credit notes linked" description="Create a credit note from this RMA when the supplier issues one." testId="credit-empty" />}
      {cns.map((cn) => (
        <div key={cn.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-mono text-base font-bold text-slate-900">{cn.cn_number}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{cn.status}</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            <Row label="Supplier" value={cn.supplier_name} />
            <Row label="Original" value={money(cn.original_amount, cn.currency)} />
            <Row label="Used" value={money(cn.used_amount, cn.currency)} />
            <Row label="Available" value={money(cn.available_balance, cn.currency)} />
          </div>
          {cn.pdf_path && <a href={fileUrl(cn.pdf_path)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"><FileText className="h-4 w-4" /> View CN PDF</a>}
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Add Credit Note</DialogTitle></DialogHeader>
          <div className="mb-1 rounded-lg bg-blue-50 p-3 text-xs text-slate-600">
            Linked to <span className="font-mono font-semibold">{c.rma_number}</span> • {c.product_name} • {c.serial_number} • {c.supplier_name || "No supplier"}
          </div>
          <div className="space-y-3">
            <div><Label className="mb-1 block text-xs">CN Number</Label><Input value={form.cn_number} onChange={(e) => setForm({ ...form, cn_number: e.target.value })} data-testid="rma-cn-number" placeholder="CN-88213" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1 block text-xs">Issue Date</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div><Label className="mb-1 block text-xs">Original Amount</Label><Input type="number" value={form.original_amount} onChange={(e) => setForm({ ...form, original_amount: e.target.value })} data-testid="rma-cn-amount" /></div>
            </div>
            <div><Label className="mb-1 block text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <div>
              <Label className="mb-1 block text-xs">PDF Attachment</Label>
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 hover:border-blue-400">
                <Upload className="h-4 w-4" /> {pdfFile ? pdfFile.name : "Choose CN PDF"}
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => setPdfFile(e.target.files[0])} />
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="save-rma-cn-btn" className="bg-blue-600 hover:bg-blue-700">Create Credit Note</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Timeline({ timeline, history }) {
  const events = [...timeline].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  if (events.length === 0) return <EmptyState icon={Clock} title="No timeline events" testId="timeline-empty" />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="space-y-0">
        {events.map((e, i) => (
          <div key={e.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-600 ring-4 ring-blue-50" />
              {i < events.length - 1 && <div className="w-0.5 flex-1 bg-slate-200" />}
            </div>
            <div className="pb-6">
              <p className="text-sm text-slate-800">{e.text}</p>
              <p className="mt-0.5 text-xs text-slate-400">{e.employee} • {fmtDateTime(e.timestamp)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
